// src/hooks/useActivityCleaner.js

import { useCallback } from "react";
import { doc, deleteDoc } from "firebase/firestore";
import { db } from "../config/firebase";

export const useActivityCleaner = (collectionName = "activities") => {
  const parseActivityDate = (activity) => {
    try {
      // Intentar múltiples campos de fecha
      const rawDate = activity.timestamp || activity.createdAt;
      
      if (!rawDate) {
        return null;
      }
      
      // Si es un Timestamp de Firestore
      if (rawDate?.toDate && typeof rawDate.toDate === 'function') {
        return rawDate.toDate();
      }
      
      // Si tiene estructura {seconds, nanoseconds}
      if (rawDate?.seconds !== undefined) {
        return new Date(rawDate.seconds * 1000);
      }
      
      // Si es string
      if (typeof rawDate === 'string') {
        const date = new Date(rawDate);
        return isNaN(date.getTime()) ? null : date;
      }
      
      // Si ya es Date
      if (rawDate instanceof Date) {
        return rawDate;
      }
      
      return null;
    } catch (error) {
      console.error(`❌ Error parseando fecha:`, error);
      return null;
    }
  };

  const cleanOldActivities = useCallback(async (activities) => {
    console.log("🧹 INICIANDO LIMPIEZA DE ACTIVIDADES");
    console.log(`📊 Actividades recibidas: ${activities?.length || 0}`);

    if (!activities || activities.length === 0) {
      console.log("ℹ️ No hay actividades para limpiar");
      return { deleted: 0, errors: 0, skipped: 0 };
    }

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    console.log(`📅 Fecha límite: ${sevenDaysAgo.toISOString()}`);
    console.log(`📅 Fecha límite (local): ${sevenDaysAgo.toLocaleString()}`);

    // Mostrar las primeras 3 actividades como ejemplo
    console.log("📋 Muestra de actividades:", activities.slice(0, 3).map(a => ({
      id: a.id?.substring(0, 8),
      type: a.type || a.operation,
      timestamp: a.timestamp,
      createdAt: a.createdAt,
      description: a.description?.substring(0, 50),
      entityName: a.entityName
    })));

    let deleted = 0;
    let errors = 0;
    let skipped = 0;
    let noDate = 0;
    let recentDates = 0;

    for (const activity of activities) {
      if (!activity.id) {
        skipped++;
        continue;
      }

      if (activity.isActive === false) {
        skipped++;
        continue;
      }

      const activityDate = parseActivityDate(activity);
      
      if (!activityDate) {
        noDate++;
        if (noDate <= 3) {
          console.warn(`⚠️ Actividad sin fecha válida:`, {
            id: activity.id?.substring(0, 8),
            timestamp: activity.timestamp,
            createdAt: activity.createdAt,
            type: activity.type || activity.operation
          });
        }
        continue;
      }

      // Verificar si tiene más de 7 días
      if (activityDate < sevenDaysAgo) {
        try {
          console.log(`🗑️ Eliminando: ${activity.id?.substring(0, 8)}... - ${activityDate.toISOString()} - ${activity.description?.substring(0, 50)}`);
          const docRef = doc(db, collectionName, activity.id);
          await deleteDoc(docRef);
          deleted++;
          console.log(`✅ Eliminada: ${deleted} - ${activity.entityName || 'Sin nombre'}`);
        } catch (err) {
          errors++;
          console.error(`❌ Error eliminando ${activity.id}:`, err.message || err);
        }
      } else {
        recentDates++;
      }
    }

    console.log(`📊 RESUMEN DE LIMPIEZA:`);
    console.log(`  ✅ Eliminadas: ${deleted}`);
    console.log(`  ❌ Errores: ${errors}`);
    console.log(`  ⏭️ Omitidas (isActive=false o sin ID): ${skipped}`);
    console.log(`  ⚠️ Sin fecha válida: ${noDate}`);
    console.log(`  📅 Recientes (menos de 7 días): ${recentDates}`);
    console.log(`  📦 Total procesadas: ${activities.length}`);

    return { deleted, errors, skipped };
  }, [collectionName]);

  return { cleanOldActivities };
};